<script>
import { mapGetters } from 'vuex'
import McBadge from '@/components/McBadge'
import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import { debounce } from '@/helpers/debounce'
import {
  GET_NOTIFICATION_GROUPS,
  DELETE_NOTIFICATION_GROUP,
} from '@/services/api/notification/ngroup'
import i18n from '@/i18n'

export default {
  components: {
    McBadge,
    McButton,
    McCheckbox,
  },

  props: {
    status: String,
  },

  data() {
    return {
      customHandlers: {
        handleClickRow: (item) => {
          if (!this.hasAccess(['e3a-email-notifikasi:update'])) {
            return
          }
          this.$router.push({ name: 'Email Notification Detail', params: { id: item.id } })
        },
        handleAdd: () => {
          return this.$router.push({ name: 'Email Notification Create' })
        },
      },
      isLoading: true,
      isBusy: false,
      items: [],
      ids: [],
      page: 1,
      limit: 10,
      totalCount: 0,
      search: '',
      selectedIds: {},
      selectedIdsLength: 0,
      selectedAll: false,
      limitOptions: [10, 25, 50, 100],
      listNgroups: [],
    }
  },
  computed: {
    startData() {
      return this.limit * this.page - this.limit + 1
    },
    endData() {
      const _endData = this.limit * this.page
      return this.totalCount < _endData ? this.totalCount : _endData
    },

    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      isNewSubmodule: 'identity/isNewSubmodule',
    }),
    fields() {
      return [
        {
          key: 'checkbox',
          label: '',
          headerRowspan: 2,
        },
        {
          key: 'name',
          label: this.$t('notification.setting.emailNotification.name'),
          isRedirectable: true,
          width: 30,
          headerRowspan: 2,
        },
        {
          key: 'recipient',
          label: this.$t('notification.setting.emailNotification.recipientAmount'),
          width: 60,
          children: [
            {
              key: 'whatsapp',
              label: 'WhatsApp',
              childOfIndex: 0,
              isTopHead: 0,
            },
            {
              key: 'email',
              label: this.$t('notification.setting.emailNotification.email'),
              childOfIndex: 0,
              isTopHead: 0,
            },
          ],
          headerColspan: 2,
          headerRowspan: 1,
        },
        {
          key: 'alertCode',
          label: this.$t('notification.setting.emailNotification.type'),
          width: 30,
          headerRowspan: 2,
        },
        {
          key: 'creator',
          label: this.$t('notification.setting.emailNotification.createdBy'),
          width: 30,
          headerRowspan: 2,
        },
      ]
    },
    childFields() {
      const filteredData = this.fields.filter((obj) => {
        return Object.hasOwn(obj, 'children')
      })

      return filteredData
    },
    fieldList() {
      return this.selectValues(this.fields, 'key')
    },

    lang() {
      return i18n.locale
    },
  },

  async created() {
    this.ids = this.listNgroups.map((item) => item.ngroupId)
    this.totalCount = this.ids.length
    await this.getData({
      limit: this.limit,
      page: 1,
    })
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    async getData(opts) {
      const res = await this.$apollo.query({
        query: GET_NOTIFICATION_GROUPS,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          },
        },
      })

      this.listNgroups = res.data.ngroups.ngroups.data
      this.isLoading = false

      this.ids = this.listNgroups.map((item) => item.ngroupId)
      this.totalCount = res.data.ngroups.ngroups.pagination.total
    },
    setStatus() {
      this.$store.commit('notification/SET_STATUS', this.status)
    },
    showDeleteModal() {
      this.$refs['mc-modal-delete'].show()
    },

    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
      }
      await this.getData(opts)
      this.$refs['search-input-ref'].$el.focus()
    }, 500),

    handleClearSearch() {
      this.handleSearchChange(undefined)
    },

    async handlePageChange(newPage) {
      const opts = {
        page: newPage,
        search: this.search != '' ? this.search : undefined,
        limit: this.limit,
      }
      await this.getData(opts)
    },

    selectValues(data, key) {
      const result = []

      // Recursive helper function
      function selectHelper(obj) {
        const selectedValues = obj[key]
        if (selectedValues) {
          result.push(selectedValues)
        }

        if (obj.children && Array.isArray(obj.children)) {
          obj.children.forEach((child) => selectHelper(child))
        }
      }

      data.forEach((obj) => selectHelper(obj))

      for (let i = 0; i < result.length; i++) {
        if (result[i] === 'recipient') {
          result.splice(i, 1)
        }
      }

      return result
    },

    handleAdd() {
      this.$router.push({
        name: 'Email Notification Create',
      })
    },

    handleSelect(checked, data) {
      if (this.checkType === 'single') {
        this.selectedIds = { [data.id]: true }
        this.selectedIdsLength = 1
      } else {
        this.selectedIds = { ...this.selectedIds, [data.id]: checked }
        if (checked) {
          this.selectedIdsLength += 1
        } else {
          this.selectedIdsLength -= 1
        }

        if (this.selectedIdsLength === this.ids.length) this.selectedAll = true
        else this.selectedAll = false
      }
    },

    handleSelectAll(event) {
      if (event) {
        const ids = this.ids
        this.selectedIds = Object.fromEntries(ids.map((id) => [id, true]))
        this.selectedIdsLength = this.ids.length
        this.selectedAll = true
      } else {
        this.handleUnselectAll()
      }
    },

    handleUnselectAll() {
      this.selectedIds = {}
      this.selectedAll = false
      this.selectedIdsLength = 0
    },

    handleClickRow(id) {
      this.$router.push({
        name: 'Email Notification Detail',
        params: { id: id },
      })
    },

    async handleLimitChange(_limit) {
      this.limit = _limit

      await this.getData({
        limit: _limit,
        search: this.search != '' ? this.search : undefined,
        page: 1,
      })
    },

    async handleDelete() {
      const _selected = Object.keys(this.selectedIds).filter(
        (key) => this.selectedIds[key] == true && key !== 'undefined'
      )
      this.isBusy = true
      const promises = []

      _selected.forEach((item) => {
        if (item !== 'undefined') {
          promises.push(
            this.$apollo.mutate({
              mutation: DELETE_NOTIFICATION_GROUP,
              variables: {
                input: {
                  ngroupId: item,
                },
              },
            })
          )
        }
      })

      await Promise.all(promises).then((res) => {
        if (this.search) this.handleClearSearch()
        this.handleUnselectAll()
        this.getData({
          pagination: {
            page: this.page,
            show: this.limit,
          },
        })
        this.$refs['mc-modal-delete'].hide()
        res.forEach((result) => {
          if (result) this.$mcToast.success(this.$t('global.alert.text.delete'))
          else this.$mcToast.success(this.$t('global.alert.text.failedDelete'))
        })
      }, 500)

      this.isBusy = false
    },
  },
}
</script>

<template>
  <b-tab class="border-0" @click="setStatus">
    <template #title>
      <span class="d-inline-block d-sm-none">
        <i class="fas fa-home"></i>
      </span>
      <span class="d-none d-sm-inline-block">{{
        $t('notification.setting.emailNotification.notificationRecipient')
      }}</span>
      <span v-if="isNewSubmodule('e3a-email-notifikasi')" class="float-end">
        <McBadge variant="danger" size="small" type="notif" class="mx-1" />
      </span>
    </template>

    <div class="mc-table pb-4">
      <div class="clearfix mb-3">
        <div class="float-end d-flex">
          <McButton
            v-if="selectedIdsLength"
            variant="secondary-red"
            :text="$t('global.delete')"
            icon="icon-outline_trash"
            class="mx-2 mb-2 ms-auto"
            @click="showDeleteModal"
          />
          <McButton
            variant="primary"
            class="mb-2 ms-auto"
            :disabled="isBusy"
            :text="$t('global.create')"
            icon="icon-fill_plus"
            @click="handleAdd"
          />
        </div>
      </div>

      <div>
        <b-row>
          <b-col md="4" class="mc-row-check-wrapper">
            <h6 v-if="selectedIdsLength" class="mc-row-check mt-0">
              <span class="badge btn-success">{{ selectedIdsLength }}</span>
              {{ $t('global.object.selectedRow') }}
              <a class="color-primary" @click="handleUnselectAll">{{
                $t('global.action.deselect')
              }}</a>
            </h6>
          </b-col>
          <b-col class="flex-row-reverse mc-table-view-wrapper">
            <div class="mc-table-view d-flex flex-row-reverse">
              <b-input-group size="sm" class="mc-has-search" @click="focusSearch">
                <template #prepend>
                  <i class="ps-2 mc-icon icon-fill_search" />
                </template>
                <b-form-input
                  ref="searchInput"
                  :placeholder="$t('global.search') + '...'"
                  :disabled="isBusy"
                  :value="search"
                  @input="handleSearchChange"
                />
                <template v-if="search" #append>
                  <button class="clear-search-btn" :disabled="isBusy" @click="handleClearSearch">
                    <i class="pe-2 mc-icon icon-outline_close" />
                  </button>
                </template>
              </b-input-group>
            </div>
          </b-col>
        </b-row>

        <div class="table-responsive my-3 border border-1">
          <b-skeleton-wrapper :loading="isLoading">
            <template #loading>
              <b-card>
                <b-skeleton-table
                  :rows="3"
                  :columns="fields.length"
                  :table-props="{ bordered: false, striped: true }"
                />
              </b-card>
            </template>
            <table class="table b-table mc-table--notification-recepient">
              <thead>
                <tr>
                  <th
                    v-for="field in fields"
                    id="header"
                    :key="field.key"
                    :colspan="field.headerColspan"
                    :rowspan="field.headerRowspan"
                  >
                    <div v-if="field.key === 'checkbox'">
                      <McCheckbox v-model="selectedAll" @change="handleSelectAll" />
                    </div>
                    <div v-else :style="{ 'text-align': field.headerColspan > 1 ? 'center' : '' }">
                      {{ field.label }}
                    </div>
                  </th>
                </tr>
                <tr>
                  <th
                    v-for="field in childFields[0].children"
                    :key="field.key"
                    class="sub-header"
                    :colspan="field.headerColspan"
                  >
                    {{ field.label }}
                  </th>
                </tr>
              </thead>

              <tbody v-if="listNgroups">
                <tr v-for="data in listNgroups" :key="data.ngroupId">
                  <td v-for="key in fieldList" :key="key">
                    <div v-if="key === 'checkbox'">
                      <McCheckbox
                        v-model="selectedIds[data.ngroupId]"
                        :checked="selectedIds[data.ngroupId]"
                        @change="(event) => handleSelect(event, data)"
                      />
                    </div>
                    <span
                      v-if="key === 'name'"
                      class="fw-bold text-primary td__noellipsis clickable-field"
                      @click="handleClickRow(data.ngroupId)"
                    >
                      {{ data[key] ?? '-' }}
                    </span>

                    <span v-if="key === 'whatsapp' || key === 'email'">
                      {{ data.aggregate.totalRecipients[key] }}
                    </span>

                    <span v-if="key === 'alertCode'">
                      {{
                        data.ngroup2AlertCode
                          ?.map((item) =>
                            lang === 'en'
                              ? item.alertCodeData.descriptionEng
                              : item.alertCodeData.descriptionIdn
                          )
                          .join(', ') ?? '-'
                      }}
                    </span>

                    <span v-if="key === 'creator'">
                      {{ data[key].fullname ?? '-' }}
                    </span>
                  </td>
                </tr>
              </tbody>
              <tbody v-if="listNgroups.length == 0">
                <tr>
                  <td colspan="7">
                    <div class="text-center my-4">
                      {{ $t('global.emptyTable') }}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </b-skeleton-wrapper>
        </div>

        <div class="d-flex flex-wrap flex-md-nowrap">
          <div class="mc-limit">
            <div class="__limit">
              {{ $t('global.text.displays') }}
              <b-form-select
                class="form-select form-select-sm __limit--dropdown"
                size="sm"
                :disabled="isBusy"
                :options="limitOptions"
                :value="limit"
                @input="handleLimitChange"
              />
              <div>{{ $t('global.text.data') }}</div>
            </div>

            <div class="__info">
              <strong>{{ startData }}</strong> - <strong>{{ endData }}</strong>
              {{ $t('global.object.of') }} <strong>{{ totalCount }}</strong>
              {{ $t('global.text.data') }}
            </div>
          </div>
          <div class="mc-pagination-container">
            <b-pagination
              :disabled="isBusy"
              :per-page="limit"
              :total-rows="totalCount"
              :value="page"
              @change="handlePageChange"
            />
          </div>
        </div>
      </div>

      <b-modal
        id="mc-modal-delete"
        ref="mc-modal-delete"
        class="mc-modal"
        :title="$t('global.action.delete')"
        size="md"
        centered
        scrollable
      >
        <div class="card-body p-2">
          <p>
            {{ $t('global.confirmDelete1') }} <strong>{{ selectedIdsLength }}</strong>
            {{ $t('global.confirmDelete2') }}
          </p>
        </div>
        <template #modal-footer="{ cancel }">
          <div class="d-flex">
            <McButton
              variant="secondary"
              :text="$t('global.action.cancel')"
              class="me-2"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.action.delete')" @click="handleDelete" />
          </div>
        </template>
      </b-modal>
    </div>
  </b-tab>
</template>

<style lang="scss" scoped>
@import './style.scss';

.mc-table--notification-recepient {
  .sub-header {
    width: 120px !important;

    &:last-child {
      border-right: 1px solid $teal-100;
    }
  }
}
</style>
